import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AthleteContext = createContext(null);

export const AthleteProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  const { refreshBrandingForAthlete } = useTheme();
  const [athletes, setAthletes] = useState([]);
  const [selectedAthleteIndex, setSelectedAthleteIndex] = useState(0);
  const [athletePasses, setAthletePasses] = useState({});
  const [loading, setLoading] = useState(true);

  // Current selected athlete
  const athlete = athletes[selectedAthleteIndex] || null;
  const athletePass = athlete ? athletePasses[athlete.id] : null;

  const fetchAthletes = useCallback(async () => {
    if (!user || user.role !== 'parent') {
      setLoading(false);
      return;
    }

    try {
      // Fetch all athletes
      const athletesRes = await axios.get(`${API}/athletes`, { headers: getAuthHeaders() });
      const athletesList = athletesRes.data;
      setAthletes(athletesList);

      // Fetch passes for all athletes
      const passesMap = {};
      for (const ath of athletesList) {
        try {
          const passRes = await axios.get(`${API}/parent/athlete-pass/${ath.id}`, { headers: getAuthHeaders() });
          passesMap[ath.id] = passRes.data;
        } catch (e) {
          passesMap[ath.id] = { status: ath.athlete_pass_status || 'none' };
        }
      }
      setAthletePasses(passesMap);
      
      // Refresh branding for the first athlete on initial load
      if (athletesList.length > 0) {
        refreshBrandingForAthlete(athletesList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch athletes:', error);
    }
    setLoading(false);
  }, [user, getAuthHeaders, refreshBrandingForAthlete]);

  useEffect(() => {
    fetchAthletes();
  }, [fetchAthletes]);

  const selectAthlete = (index) => {
    if (index >= 0 && index < athletes.length) {
      setSelectedAthleteIndex(index);
      // Update branding when athlete changes
      const selectedAthlete = athletes[index];
      if (selectedAthlete) {
        refreshBrandingForAthlete(selectedAthlete.id);
      }
    }
  };

  const selectAthleteById = (athleteId) => {
    const index = athletes.findIndex(a => a.id === athleteId);
    if (index !== -1) {
      setSelectedAthleteIndex(index);
      // Update branding when athlete changes
      refreshBrandingForAthlete(athleteId);
    }
  };

  const refreshAthletes = () => {
    setLoading(true);
    fetchAthletes();
  };

  const value = {
    athletes,
    athlete,
    athletePass,
    athletePasses,
    selectedAthleteIndex,
    selectAthlete,
    selectAthleteById,
    refreshAthletes,
    loading,
    hasMultipleAthletes: athletes.length > 1,
  };

  return (
    <AthleteContext.Provider value={value}>
      {children}
    </AthleteContext.Provider>
  );
};

export const useAthlete = () => {
  const context = useContext(AthleteContext);
  if (!context) {
    throw new Error('useAthlete must be used within an AthleteProvider');
  }
  return context;
};

export default AthleteContext;
